import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';

import Tooltip from '../../misc/Tooltip';

import Icon from '../../misc/Icon';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { ActionToolbar, ImageManagerDropzone } from '../../misc/ImageManager';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}

  #image-manager-action-toolbar {
    display: ${(props) => (!props.autoShow ? 'none' : 'block')};
  }

  &:hover {
    #image-manager-action-toolbar {
      display: block;
      svg{
        margin-left: 0px;
        margin-right: 0px;
      }
    }
  }

`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right:0;
  top:0;
  z-index:99
`;

const StyleFileViewer = styled.span`
  display: flex;
  max-width: 200px;
  padding: 10px 4px;
  align-items: center;
  border: ${(props) => (props.border ? props.border : '')};
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '')};
  margin: ${(props) => (props.padding ? `${props.padding}px` : '0px')};
  
  
  svg{
    margin-left: 4px;
    margin-right: 4px;
  }

  #image-manager-action-toolbar {
    svg{
      margin-left: 0px;
      margin-right: 0px;
    }
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  }
`;

const StyledSpan = styled.span`
  margin-left: 5px;
  margin-right: 5px;
  margin-right: 8px;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
`;

const StyledFileViewer = styled.div`
  display:flex;
  flex-wrap: wrap;
  ${(props) => {

    if (!props.multiple) {
      return '';
    } else {
      return `  
        scrollbar-gutter: stable;
        margin-right: -21px;
        overflow-y: auto;
      `;
    }

  }}
`;

const FileField = ({

  fieldId,
  fieldIdentifier,
  fieldPositionId,

  title,
  value,
  multi,

  fontColor,
  margin,
  padding,
  borderRadius,
  borderWidth,
  borderColor,
  backgroundColor,
  primaryColor,
  secondaryColor,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  disabled,
  isMobileView,

  onUpload,
  onChange,
  onFocus,

  tipTitle,
  tipDescription,
  tipVisible,

  //Used for calculating width and height of element
  measure,
  onMeasureCompleted,

  type,

}) => {

  const theme = useContext(ThemeContext);

  const [wrapperRef, setWrapperRef] = useState(null);
  const [loadedFiles, setLoadedFiles] = useState(value && value.length);

  useEffect(() => {
    if (onMeasureCompleted && wrapperRef && loadedFiles) {
      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, wrapperRef.getBoundingClientRect());
    }
  }, [wrapperRef, loadedFiles]);

  const handleDrop = (files) => {
    if (onUpload) onUpload(fieldId, fieldIdentifier, fieldPositionId, files);
  };

  const handleDelete = (i) => {

    const nextFileValues = [...value];

    nextFileValues.splice(i, 1);

    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextFileValues);

  };

  const handleFocus = () => {
    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});
  };

  const handleLoadCompleted = () => {
    setLoadedFiles(true);
  };

  const handlePreview = (file) => {

    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});

    window.open(file.url, '_blank');

  };

  const filePillNode = !value ? null : value.map((item) => {

    const border = `${borderWidth}px solid ${borderColor}`;

    return (
      <StyleFileViewer
        key={item._id}
        border={border}
        borderRadius={borderRadius}
        padding={padding}
        autoShow={isMobileView}
      >
        <StyledSpan>
          {' '}
          {item.fileName}
          {' '}
        </StyledSpan>
        {item && item.url ? (<Icon icon={ThemeIcons.misc.preview} onClick={() => handlePreview(item)} />) : null}
        {!disabled ? (<Icon icon={ThemeIcons.settings.delete} color={theme.dangerColor} onClick={handleDelete} />) : null}
        <ActionToolbar
          {...{
            type,
            multiple: multi,
            onDrop: handleDrop,
            onFocus: handleFocus,
            disabled,
            isMobileView,
            ...theme
          }}
        />
      </StyleFileViewer>

    );
  });

  return (
    <StyledWrapper
      {...{
        margin,
        measure
      }}
      autoShow={isMobileView}
      ref={(e) => {
        if (e && !wrapperRef) setWrapperRef(e);
      }}
    >
      <FieldTitle
        {...{
          title,
          titleDisplay,
          titleFontSize,
          titleFontColor,
          titleTextAlign,
          titleFontStyle,
          titleFontWeight,
          titleTextTransform,
          titleTextDecoration,
        }}
      />
      {disabled ? null : (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      )}
      {
        !value || (value && value.length < 1) ? (
          <ImageManagerDropzone
            {...{
              type,
              disabled,
              multiple: multi,
              onDrop: handleDrop,
              onFocus: handleFocus,
              onLoadCompleted: handleLoadCompleted,

              fontColor,
              borderColor,
              borderWidth,
              borderRadius,
              backgroundColor,
              primaryColor,
              secondaryColor

            }}
          />

        ) : null
      }
      <StyledFileViewer
        multiple={multi}
      >
        {filePillNode}
      </StyledFileViewer>

    </StyledWrapper>
  );

};

export default React.memo(FileField);
